import {getVm} from './vm';
import {getWorkspace} from './workspace';
import throttle from 'lodash.throttle';
import event, {EVENTS} from '../lib/event';
import {blocksInfo} from './blocks';
import {ZINDEX_FOCUS_COVER} from '../lib/constant';

/**
 * workspace 安全 undo 方法，workspace undo 时，会触发块的 move、create、delete 等事件，如果在事件未结束前执行
 * 操作块的代码，可能会导致 bug
 * @param {function} before - 在 undo 前需要做的事情，可选
 * @returns {promise} Promise - 安全 undo 后返回的 Promise
 */
const workspaceSafeUndo = before => new Promise(resolve => {
    const workspace = getWorkspace();
    const events = ['move', 'create', 'delete'];
    let timer;
    const func = () => {
        if (timer) {
            clearTimeout(timer);
        }
        timer = setTimeout(() => {
            events.forEach(e => {
                event.off(e, func);
            });
            console.log('safe undo');
            resolve(true);
        }, 200);
    };
    events.forEach(e => {
        event.on(e, func);
    });
    if (before) {
        before();
    }
    console.log('undo');
    workspace.undo();
    timer = setTimeout(() => {
        console.log('safe undo');
        resolve(true);
    }, 200);
});

/**
 * 是否支持 touch 事件
 */
const useTouchInteraction = 'ontouchend' in document.body;

/**
 * 点击事件封装，支持touch和非touch模式
 * @param {dom} element 被点击的元素
 * @param {function} handler 点击处理函数
 * @param isMulti 是否持续监听
 */
const onClick = (element, handler, isMulti) => {
    const _handler = e => {
        if (useTouchInteraction) {
            if (Date.now() - touchstartTime > 500) return;
            if (!isMulti) {
                element.removeEventListener('touchstart', touchstartHandler, true);
                element.removeEventListener('touchend', _handler, true);
            }
        } else {
            if (!isMulti) {
                element.removeEventListener('click', _handler, true);
            }
        }
        setTimeout(()=>{
            handler(e)
        }, 400); //等待点击动作执行完毕
    };

    let touchstartTime = 0;
    const touchstartHandler = e => {
        touchstartTime = Date.now();
    }

    if (useTouchInteraction) {
        element.addEventListener('touchstart', touchstartHandler, true);
        element.addEventListener('touchend', _handler, true);
        event.once(EVENTS.STEP_ABORT, ()=>{
            element.removeEventListener('touchstart', touchstartHandler, true);
            element.removeEventListener('touchend', _handler, true);
        });
    } else {
        //element.addEventListener('mouseup', _handler, true);
        element.addEventListener('click', _handler, true);
        event.once(EVENTS.STEP_ABORT, ()=>{
            element.removeEventListener('mouseup', _handler, true)
        });
    }

};

/**
 * 模拟点击事件封装，支持touch和非touch模式
 * @param {dom} element 被点击的元素
 * @param {function} handler 点击处理函数
 */
const onTap = (element, handler) => {
    const _handler = e => {
        if (useTouchInteraction) {
            element.removeEventListener('touchend', _handler, true);
        } else {
            element.removeEventListener('mouseup', _handler, true);
        }
        setTimeout(()=>handler(e), 400); //等待点击动作执行完毕
    };
    if (useTouchInteraction) {
        element.addEventListener('touchend', _handler, true);
        event.once(EVENTS.STEP_ABORT, ()=>element.removeEventListener('touchend', _handler, true));
    } else {
        element.addEventListener('mouseup', _handler, true);
        event.once(EVENTS.STEP_ABORT, ()=>element.removeEventListener('mouseup', _handler, true));
    }
};

/**
 * 发送广播消息
 * @param {string} broadcastName - 📢消息名称
 */
const sendBroadcast = broadcastName => {
    const vm = getVm();
    vm.runtime.startHats('event_whenbroadcastreceived', {BROADCAST_OPTION: broadcastName});
};

/**
 * 保存 workspace 中的块的 xml 对象
 * @returns {string} Blocks xml 对象
 */
const getBlocksXml = () => {
    const {Xml: {domToText, workspaceToDom}} = window.Blockly;
    const workspace = getWorkspace();
    const res = domToText(workspaceToDom(workspace));
    const parser = new DOMParser();
    return parser.parseFromString(res, 'text/xml');
};

/**
 * 传入块的 xml，加载游戏
 * @param {*} xml - 块的 xml 对象
 */
const loadFromBlocksXml = xml => {
    const {Xml: {textToDom, clearWorkspaceAndLoadFromXml}} = window.Blockly;
    const workspace = getWorkspace();
    const xmlStr = new XMLSerializer().serializeToString(xml);
    clearWorkspaceAndLoadFromXml(textToDom(xmlStr), workspace);
};

let div;
//let startFlag = true;
const throttleTime = 1000;
const minDistance = 50; // from 和 to 间距小于 100 时，不显示动画

/**
 * 从 style transform 字符串中获取 translate 的值
 * @param {string} str transform 字符串
 * @returns {any} 数组
 */
const getTranslatePixelFromStr = str => {
    const match = str.match(/translate\((\d+\.?\d+?)px,\s*(\d+\.?\d+?)px\)/);
    if (match) {
        return [+match[1], +match[2]];
    }
    return null;
};

/**
 * 生成从 from 点到 to 点的动画效果，这个方法最大频率 1s 一次，所以动画时长需要在 1s 内
 * @param {object} from - 起始点坐标
 * @param {object} to - 终止点坐标

 */
const createAnimate = throttle((from, to) => {
    // if (!startFlag) {
    //     console.log("started");
    //     return;
    // }
    const animateDiv = () => {
        /*
        if (
            (Math.abs(from.left - to.left) < minDistance * 1.5) &&
            (Math.abs(from.top - to.top) < minDistance)
        ) {
            if (div) {
                div.style.opacity = 0;
            }
            return;
        }
        if (div && div.style.opacity === '0') {
            div.style.opacity = 0.3;
        }
        */
        setTimeout(()=>{
            if (div) {
                div.style.transition = 'all ' + (throttleTime/1000) + 's';
                div.style.opacity = 0.3;
                div.style.transform = `translate(${to.left}px, ${to.top}px)`;
            }
        }, 100);

        /*
        animate(from.left, to.left, now => {
            if (div) {
                const [_left, top] = getTranslatePixelFromStr(div.style.transform);
                div.style.transform = `translate(${now}px, ${top}px)`;
            }
        }, {spendTime: throttleTime - 100, animationFunc: 'easeInOutQuad'});
        animate(from.top, to.top, now => {
            if (div) {
                const [left, _top] = getTranslatePixelFromStr(div.style.transform);
                div.style.transform = `translate(${left}px, ${now}px)`;
            }
        }, {spendTime: throttleTime - 100, animationFunc: 'easeInOutQuad'});
        */
    };
    if (div) {
        div.style.transition = '';
        div.style.opacity = 0;
        div.style.transform = `translate(${from.left}px, ${from.top}px)`;
        animateDiv();
    } else {
        div = document.createElement('div');
        div.style.transform = `translate(${from.left}px, ${from.top}px)`;
        div.style.left = '0px';
        div.style.top = '0px';
        div.style.width = `${from.width}px`;
        div.style.height = `${from.height}px`;
        div.style.position = 'fixed';
        div.style.zIndex = ZINDEX_FOCUS_COVER + 1;
        div.style.pointerEvents = 'none';
        div.style.border = '2px solid';
        div.style.borderRadius = '5px';

        div.style.opacity = 0;
        div.style.backgroundColor = '#aaa';
        div.style.willChange = 'transform';
        document.body.appendChild(div);
        animateDiv();
    }
}, throttleTime);

const endAnimate = () => {
    if (div) {
        createAnimate.cancel();
        document.body.removeChild(div);
        div = null;
        //startFlag = true;
        // setTimeout(() => {
        //     startFlag = true;
        // }, throttleTime + 500);
    }
};

/**
 * 解析translate的坐标内容
 */
const parseTranslate = (str) => {
    let translate = (x, y) => {
        return {x: x, y: y};
    }
    return eval(str);
}

/**
 * 移动块到某个位置
 * */
const moveBlockPosition = (block, x, y) => {
    if (block) block.moveBy(x - block.getRelativeToSurfaceXY().x, y - block.getRelativeToSurfaceXY().y);
}




const getObjectValueByIndex = (obj, index) => {
    let i = 0;
    for (let key in obj) {
        if (i >= index) return obj[key];
        ++i;
    }
    return null;
}
const isNumber = (val)=>{
    if (val == undefined) return false;
    var regPos = /^\d+(\.\d+)?$/; //非负浮点数
    var regNeg = /^(-(([0-9]+\.[0-9]*[1-9][0-9]*)|([0-9]*[1-9][0-9]*\.[0-9]+)|([0-9]*[1-9][0-9]*)))$/; //负浮点数
    if(regPos.test(val) || regNeg.test(val)){
        return true;
    }else{
        return false;
    }

}

/**
 * 判断data-argument-type属性是否为下拉类型
 * @param type
 * @returns {boolean}
 */
const isDropdownType = (type) => {
    return type === 'dropdown' || type === 'variable';
}

window.getBlocksXml = getBlocksXml;
window.loadFromBlocksXml = loadFromBlocksXml;

export {
    endAnimate,
    createAnimate,
    sendBroadcast,
    getBlocksXml,
    loadFromBlocksXml,
    useTouchInteraction,
    onClick,
    onTap,
    workspaceSafeUndo,
    parseTranslate,
    moveBlockPosition,
    isNumber,
    isDropdownType,
    getObjectValueByIndex
};
